import type { AnnotationPrimitive } from './annotationGeometry'

export interface AnnotationSelectionBounds {
  x: number
  y: number
  width: number
  height: number
}

function primitiveBounds(primitive: AnnotationPrimitive): AnnotationSelectionBounds {
  switch (primitive.shape) {
    case 'polyline':
    case 'polygon': {
      const xs = primitive.points.map((point) => point.x)
      const ys = primitive.points.map((point) => point.y)
      return {
        x: Math.min(...xs),
        y: Math.min(...ys),
        width: Math.max(...xs) - Math.min(...xs),
        height: Math.max(...ys) - Math.min(...ys),
      }
    }
    case 'rect':
      return {
        x: primitive.x,
        y: primitive.y,
        width: primitive.width,
        height: primitive.height,
      }
    case 'ellipse':
      return {
        x: primitive.cx - primitive.rx,
        y: primitive.cy - primitive.ry,
        width: primitive.rx * 2,
        height: primitive.ry * 2,
      }
    case 'dot':
      return {
        x: primitive.cx - primitive.r,
        y: primitive.cy - primitive.r,
        width: primitive.r * 2,
        height: primitive.r * 2,
      }
    case 'text': {
      const approximateAdvance = Array.from(primitive.text).reduce((width, character) => {
        // CJK/Hangul glyphs are approximately one em; Latin averages narrower.
        return width + ((character.codePointAt(0) ?? 0) > 0x02ff ? 1 : 0.62)
      }, 0)
      return {
        x: primitive.x,
        y: primitive.y,
        width: Math.max(primitive.fontSize, approximateAdvance * primitive.fontSize),
        height: primitive.fontSize,
      }
    }
  }
}

function unionBounds(bounds: AnnotationSelectionBounds[]): AnnotationSelectionBounds | null {
  if (bounds.length === 0) return null
  const x0 = Math.min(...bounds.map((box) => box.x))
  const y0 = Math.min(...bounds.map((box) => box.y))
  const x1 = Math.max(...bounds.map((box) => box.x + box.width))
  const y1 = Math.max(...bounds.map((box) => box.y + box.height))
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 }
}

function padBounds(bounds: AnnotationSelectionBounds, padding: number): AnnotationSelectionBounds {
  return {
    x: bounds.x - padding,
    y: bounds.y - padding,
    width: bounds.width + padding * 2,
    height: bounds.height + padding * 2,
  }
}

/**
 * Measure the browser's rendered SVG glyph geometry. The primitive fallback is
 * deterministic for jsdom and for the brief frame before a browser exposes
 * `getBBox`; real text selection uses the loaded font's exact glyph bounds.
 */
export function measureAnnotationSelectionBounds(
  svg: SVGSVGElement | null,
  primitives: AnnotationPrimitive[],
  selectedId: string,
  padding: number,
): AnnotationSelectionBounds | null {
  const measured: AnnotationSelectionBounds[] = []
  if (svg) {
    for (const element of svg.querySelectorAll<SVGGraphicsElement>('[data-annotation-id]')) {
      if (element.getAttribute('data-annotation-id') !== selectedId) continue
      if (typeof element.getBBox !== 'function') continue
      try {
        const box = element.getBBox()
        if ([box.x, box.y, box.width, box.height].every(Number.isFinite)) {
          measured.push({
            x: box.x,
            y: box.y,
            width: box.width,
            height: box.height,
          })
        }
      } catch {
        // Detached/unsupported SVG elements (notably jsdom) use the fallback.
      }
    }
  }

  const fallback = primitives
    .filter((primitive) => primitive.id === selectedId)
    .map(primitiveBounds)
  const bounds = unionBounds(measured.length > 0 ? measured : fallback)
  return bounds ? padBounds(bounds, padding) : null
}
